
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function PrePurchaseElectricalInspectionsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Pre-Purchase Electrical Inspections',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Pre-purchase electrical inspections for home buyers in Fresno, CA. Identify electrical issues, safety hazards, and costly repairs before you buy.',
    url: `${siteUrl}/services/testing-certification/pre-purchase-electrical-inspections`,
  };

  const features = [
    {
      icon: 'ri-home-heart-line',
      title: 'Buyer Protection',
      description:
        'Identify electrical problems before closing so you can negotiate repairs or pricing',
    },
    {
      icon: 'ri-search-eye-line',
      title: 'Hidden Issue Detection',
      description:
        'Uncover electrical hazards and code violations that general home inspectors often miss',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Cost Estimation',
      description:
        'Get accurate repair cost estimates for electrical issues found during inspection',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Safety Assessment',
      description:
        'Evaluate fire risks, shock hazards, and dangerous electrical conditions in the property',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Code Compliance Check',
      description:
        'Verify the electrical system meets California Title 24 and current NEC standards',
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Thermal Imaging',
      description:
        'Infrared scans detect overheating circuits, loose connections, and hidden problems',
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Fast Turnaround',
      description:
        'Same-day or next-day inspections and reports to meet your closing timeline',
    },
    {
      icon: 'ri-file-text-line',
      title: 'Detailed Buyer Report',
      description:
        'Comprehensive report with photos, findings, and recommendations for negotiations',
    },
  ];

  const whatsIncluded = [
    'Licensed C-10 electrician inspection',
    'Complete electrical system assessment',
    'Panel, breaker, and circuit evaluation',
    'Wiring condition and age verification',
    'GFCI/AFCI protection compliance check',
    'Outlet, switch, and fixture testing',
    'Thermal imaging scans',
    'Safety hazard identification',
    'Repair cost estimates',
    'Detailed buyer report with photos',
  ];

  return (
    <>
      <SEOHead
        title="Pre-Purchase Electrical Inspections Fresno CA | Home Buyer Electrical Testing"
        description="Pre-purchase electrical inspections for Fresno home buyers. Licensed electricians identify electrical issues, safety hazards, and repair costs before you buy. Fast reports."
        canonical="/services/testing-certification/pre-purchase-electrical-inspections"
        keywords="pre-purchase electrical inspection Fresno, home buyer electrical inspection, electrical inspection before buying, Fresno home inspection"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Pre-Purchase Electrical Inspections — Fresno, CA"
        heroSubtitle="Know what you're buying — comprehensive electrical inspections for Fresno home buyers"
        heroImage="https://public.readdy.ai/ai/img_res/340c3dd15e9f97094d718aad8a505672.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20of%20home%20inspection%20report%20clipboard%20with%20electrical%20checklist%20next%20to%20digital%20multimeter%20and%20thermal%20camera%20on%20table%20in%20front%20of%20residential%20electrical%20panel%20bright%20California%20home%20setting&width=700&height=600&seq=prepurch-fresno-001&orientation=portrait"
        fresnoContent="Buying a home in Fresno? Don't skip the electrical inspection. Our licensed electricians provide thorough pre-purchase electrical inspections for buyers throughout Fresno County — from Woodward Park to Tower District to Clovis. We identify safety hazards, code violations, outdated wiring, and costly electrical repairs before you close. Our detailed reports give you the information you need to negotiate repairs, adjust your offer, or walk away with confidence."
        features={features}
        whatsIncluded={whatsIncluded}
        faqs={[
          {
            question: 'Why do I need a pre-purchase electrical inspection in Fresno?',
            answer:
              'General home inspections often miss electrical problems. Our specialized electrical inspections identify safety hazards, code violations, outdated wiring, insufficient panel capacity, and hidden issues that could cost thousands to repair. Fresno buyers use our reports to negotiate repairs, adjust offers, or avoid buying homes with dangerous electrical systems.',
          },
          {
            question: 'How much does a pre-purchase electrical inspection cost in Fresno?',
            answer:
              'Pre-purchase electrical inspections in Fresno typically cost $250–$450 depending on home size and system complexity. This small investment can save you thousands by identifying electrical problems before closing. We provide fast turnaround reports to meet your escrow timeline and include repair cost estimates for negotiations.',
          },
          {
            question: 'What does a pre-purchase electrical inspection in Fresno include?',
            answer:
              'Our Fresno pre-purchase inspections cover the electrical panel, circuit breakers, wiring condition and age, grounding systems, outlets, switches, GFCI/AFCI protection, lighting fixtures, and code compliance. We use thermal imaging to detect hidden problems and provide a detailed report with photos, findings, safety concerns, and repair cost estimates.',
          },
          {
            question: 'How long does a pre-purchase electrical inspection take in Fresno?',
            answer:
              'Most pre-purchase inspections take 1.5–3 hours depending on home size and electrical system complexity. We offer same-day or next-day scheduling to meet your closing timeline, and provide detailed reports within 24 hours so you can make informed decisions quickly during your Fresno home purchase.',
          },
          {
            question: 'What are common electrical problems found in Fresno homes during pre-purchase inspections?',
            answer:
              "Common issues we find include outdated electrical panels (Federal Pacific, Zinsco), insufficient panel capacity for modern loads, missing GFCI/AFCI protection, aluminum wiring, knob-and-tube wiring, improper DIY electrical work, overloaded circuits, and systems that don't meet California Title 24 standards. Our inspections identify these problems before you buy.",
          },
          {
            question: 'Can I use your inspection report to negotiate with the seller in Fresno?',
            answer:
              'Absolutely. Our detailed reports document all electrical issues, safety hazards, and code violations with photos and repair cost estimates. Fresno buyers use our reports to negotiate seller repairs, request closing cost credits, or adjust their purchase offers. Our licensed C-10 credentials give the report credibility with sellers and real estate agents.',
          },
        ]}
      />
    </>
  );
}
